import { IInspectionTemplateDetails } from "../store/types";
import Api from "../../../services/api";
import { TMultiCallParam } from "../../../utils/api.interfaces";
import { IScopeGroupSearch } from "../utils/templateScopeGroups";

export class InspectionTemplateService {
    constructor(private readonly api: Api) {}

    /**
     * Get templates for a specific asset type group ID.
     * @param assetTypeGroupId - The group ID (e.g., "GroupVehicleId", "GroupTrailerId", or custom group ID)
     * @param scopeGroups - The asset's groups (see getAssetTemplateScopeGroups). Templates scoped to
     * other groups are excluded; fleet-wide templates are always returned.
     */
    async getTemplatesForAssetType(assetTypeGroupId: string, scopeGroups?: IScopeGroupSearch[]): Promise<IInspectionTemplateDetails[]> {
        return this.searchTemplates({ assetType: [assetTypeGroupId], ...(scopeGroups?.length && { groups: scopeGroups }) });
    }

    /**
     * Get templates directly linked to a specific device (InspectionTemplate.LinkedAssets),
     * independent of the device's asset-type group.
     * @param deviceId - The device id
     * @param scopeGroups - The asset's groups (see getAssetTemplateScopeGroups). A linked template
     * scoped to other groups is excluded.
     */
    async getTemplatesForAsset(deviceId: string, scopeGroups?: IScopeGroupSearch[]): Promise<IInspectionTemplateDetails[]> {
        return this.searchTemplates({ linkedAssets: [{ id: deviceId }], ...(scopeGroups?.length && { groups: scopeGroups }) });
    }

    /**
     * Get full template details including InspectionParts and InspectionItems.
     * One standard Get returns the whole nested tree: out-of-the-box templates are small (Vehicle: 12
     * sections of 1-10 items, Trailer: 8 sections of 1-8 items), so a single round trip beats
     * separate sections/items requests.
     *
     * @param templateId - The template ID
     */
    async getTemplateDetails(templateId: string): Promise<IInspectionTemplateDetails> {
        const templates = await this.api.callPromise("Get", {
            typeName: "InspectionTemplate",
            search: {
                id: templateId
            }
        });

        if (!templates || templates.length === 0) {
            throw new Error(`Template with id ${ templateId } not found`);
        }

        return templates[0];
    }

    /**
     * Full template details for several template IDs in one round trip — one generic Get per ID,
     * batched in a multiCall, because InspectionTemplate has no search by several IDs.
     *
     * Rejects when the multiCall fails; a template the server does not return is absent from the map.
     */
    async getTemplateDetailsByIds(templateIds: readonly string[]): Promise<Map<string, IInspectionTemplateDetails>> {
        const templateById = new Map<string, IInspectionTemplateDetails>();
        if (templateIds.length === 0) {
            return templateById;
        }
        const calls: TMultiCallParam[] = templateIds.map(id => ["Get", { typeName: "InspectionTemplate", search: { id } }]);
        const results = await this.api.multiCallPromise(calls);
        templateIds.forEach((id, index) => {
            const template = ((results?.[index] ?? []) as IInspectionTemplateDetails[])[0];
            if (template) {
                templateById.set(id, template);
            }
        });
        return templateById;
    }

    /**
     * Get photo slots for a specific template ID.
     * Uses GetInspectionTemplates (not the generic Get) because it populates
     * overallPhotoSlots — the generic Get API does not call PopulateSlotsAsync.
     */
    async getTemplatePhotoSlots(templateId: string): Promise<IInspectionTemplateDetails["overallPhotoSlots"]> {
        const templates = await this.api.callPromise("GetInspectionTemplates", {
            search: { id: templateId }
        });
        return (templates ?? [])[0]?.overallPhotoSlots;
    }

    /**
     * Runs a GetInspectionTemplates search and filters out archived results.
     * Only an explicit isArchived=true hides a template; when the field is absent it is shown.
     */
    private async searchTemplates(search: Record<string, unknown>): Promise<IInspectionTemplateDetails[]> {
        const templates = await this.api.callPromise("GetInspectionTemplates", { search });
        return (templates || []).filter((t: IInspectionTemplateDetails) => t.isArchived !== true);
    }
}
